import { formatNumber } from '../../../utils';
import { Area, AreaChart, CartesianGrid, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SmallText } from '@sb1/ffe-core-react';
import { Card } from '../../../components/card/Card';
import { DARK_GREY, FJELL_70, FROST } from './StatisticsColors';
import moment from 'moment';
import { ContextErrorMessage, ContextInfoMessage } from '@sb1/ffe-context-message-react';
import Loading from '../../../components/Loading';
import styles from './organization-statistics-chart.module.less';

const OrganizationStatisticsChart = ({
    organizationId,
    from,
    to,
    setFrom,
    setTo,
    tooltipFormatFunction,
    fetchData,
    title,
    contextMessage,
    errorMessage,
    yAxisLabel,
    dataKey,
}) => {
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload?.length > 0) {
            return (
                <div className={styles.tooltip}>
                    <div>Dato: {payload[0].payload.date}</div>
                    <div>
                        {payload[0].name}: {tooltipFormatFunction(payload[0].value)}
                    </div>
                </div>
            );
        }
        return null;
    };

    const [statistics, setStatistics] = useState([]);
    const baseURL = useSelector((s) => s.baseURL);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (moment(from, 'DD.MM.YYYY', true).isValid() && moment(to, 'DD.MM.YYYY', true).isValid()) {
            setStatistics([]);
            setLoading(true);
            fetchData({ baseURL, organizationId, from, to })
                .then((stats) => {
                    setStatistics(stats);
                })
                .finally(() => setLoading(false));
        }
    }, [organizationId, from, to, baseURL]);

    const yAxisWidth = () => {
        let maxWidth = 3;
        if (statistics?.length) {
            for (const statistic of statistics) {
                maxWidth = Math.max(maxWidth, formatNumber(statistic[dataKey]).length);
            }
        }
        return maxWidth * 9.1;
    };

    const [leftArea, setLeftArea] = useState();
    const [rightArea, setRightArea] = useState();

    return (
        <Card title={title}>
            {contextMessage && <ContextInfoMessage compact>{contextMessage}</ContextInfoMessage>}
            {errorMessage && <ContextErrorMessage compact>{errorMessage}</ContextErrorMessage>}
            {statistics?.length === 0 && loading ? (
                <Loading />
            ) : (
                <div className={styles.graphContainer}>
                    <SmallText className={styles.yAxisLabel}>{yAxisLabel}</SmallText>
                    <ResponsiveContainer>
                        <AreaChart
                            data={statistics}
                            onMouseDown={(e) => {
                                setLeftArea(e?.activeLabel);
                            }}
                            onMouseMove={(e) => {
                                if (leftArea && e?.activeLabel) {
                                    setRightArea(e.activeLabel);
                                }
                            }}
                            onMouseUp={() => {
                                if (leftArea === rightArea) {
                                    return;
                                }
                                if (moment(leftArea, 'DD.MM.YYYY').isBefore(moment(rightArea, 'DD.MM.YYYY'))) {
                                    setFrom(leftArea);
                                    setTo(rightArea);
                                } else {
                                    setFrom(rightArea);
                                    setTo(leftArea);
                                }
                                setRightArea(undefined);
                                setLeftArea(undefined);
                            }}
                        >
                            <defs>
                                <linearGradient id={'areaFill'} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="10%" stopColor={FROST} stopOpacity={0.9} />
                                    <stop offset="90%" stopColor={FROST} stopOpacity={0.1} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray={'3'} />
                            <XAxis stroke={DARK_GREY} dataKey={'date'} fontSize={'0.875rem'} />
                            <YAxis
                                width={yAxisWidth()}
                                stroke={DARK_GREY}
                                dataKey={dataKey}
                                allowDecimals={false}
                                tickFormatter={(value) => {
                                    if (value === 0) {
                                        return '';
                                    }
                                    return formatNumber(value);
                                }}
                                fontSize={'0.875rem'}
                            />
                            <Area
                                type="basis"
                                dataKey={dataKey}
                                stroke={FJELL_70}
                                fill="url(#areaFill)"
                                isAnimationActive={false}
                            />
                            {leftArea && rightArea && (
                                <ReferenceArea x1={leftArea} x2={rightArea} strokeOpacity={0.3} />
                            )}
                            <Tooltip
                                cursor={{
                                    strokeDasharray: '3 3',
                                }}
                                content={<CustomTooltip />}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </Card>
    );
};

export default OrganizationStatisticsChart;
